import * as PIXI from 'pixi.js';
import Background from './Background';
import Ground from './Ground';
import Config from './Config';
import Player from './Player';
import Treasure from './Treasure';
import Obstacle from './Obstacle';
import Foreground from './Foreground';
import Health from './Health';
import Energy from './Energy';
import Life from './Life';
import SoundButton from './SoundButton';
import * as Sounds from './Sounds';

PIXI.utils.skipHello();

export default class Application extends PIXI.Application {
  constructor(options, onEnd = () => {}) {
    super({
      ...options,
      width: Config.GAME_WIDTH,
      height: Config.GAME_HEIGHT,
      autoStart: false,
    });

    Sounds.music.play();

    this.maxPlayerDistance = 0;
    this.lastCellIndex = 0;
    this.lastObstacleCellIndex = -5;

    this.background = new Background();
    this.ground = new Ground();
    this.treasures = new PIXI.Container();
    this.obstacles = new PIXI.Container();
    this.player = new Player((score) => {
      Sounds.music.stop();
      Sounds.end.play();

      onEnd(score);
    });
    this.foreground = new Foreground();
    this.health = new Health();
    this.energy = new Energy();
    this.soundButton = new SoundButton();

    this.lastScoreShown = 0;
    this.score = new PIXI.Text('', {
      fill: 0xffffff,
      fontFamily: ['Roboto', 'Arial', 'Helvetica'],
      fontWeight: 'bold',
      fontSize: 72,
      align: 'center',
    });
    this.score.alpha = 0.75;
    this.score.anchor.set(0.5, 0);
    this.score.position.set(Config.GAME_WIDTH / 2, 200);

    // this.viewport = new Viewport({
    //   screenWidth: Config.GAME_WIDTH,
    //   screenHeight: Config.GAME_HEIGHT,
    //   worldWidth: Config.GAME_WIDTH,
    //   worldHeight: Config.GAME_HEIGHT,
    //   events: this.renderer.events,
    //   disableOnContextMenu: true,
    // });

    this.foreground.interactive = true;
    this.foreground.on('pointerdown', () => this.player.jump());

    this.stage.addChild(this.background);
    this.stage.addChild(this.ground);
    // this.stage.addChild(this.viewport);
    this.stage.addChild(this.treasures);
    this.stage.addChild(this.obstacles);
    this.stage.addChild(this.player);
    this.stage.addChild(this.foreground);
    this.stage.addChild(this.health);
    this.stage.addChild(this.energy);
    this.stage.addChild(this.score);
    this.stage.addChild(this.soundButton);

    this.ticker.add(() => {
      const dt = this.ticker.deltaMS / 1000;
      const smoothDt = Math.min(dt, 1 / 60);

      this.update(smoothDt);
    });

    PIXI.Loader.shared.resources.soundOn || PIXI.Loader.shared.add('soundOn', 'game/sound-on.png');
    PIXI.Loader.shared.resources.soundOff || PIXI.Loader.shared.add('soundOff', 'game/sound-off.png');
    PIXI.Loader.shared.resources.player || PIXI.Loader.shared.add('player', 'game/pufek.json');
    PIXI.Loader.shared.resources.treasure || PIXI.Loader.shared.add('treasure', 'game/crystal.json');
    PIXI.Loader.shared.resources.bg5 || PIXI.Loader.shared.add('bg5', 'game/bg5@0.5x.png');
    PIXI.Loader.shared.resources.bg4 || PIXI.Loader.shared.add('bg4', 'game/bg4@0.5x.png');
    PIXI.Loader.shared.resources.bg3 || PIXI.Loader.shared.add('bg3', 'game/bg3@0.5x.png');
    // PIXI.Loader.shared.resources.bg2 || PIXI.Loader.shared.add('bg2', 'game/bg2@0.5x.png');
    PIXI.Loader.shared.resources.bg1 || PIXI.Loader.shared.add('bg1', 'game/bg1@0.5x.png');
    PIXI.Loader.shared.resources.ground || PIXI.Loader.shared.add('ground', 'game/ground.png');
    PIXI.Loader.shared.resources.fg1 || PIXI.Loader.shared.add('fg1', 'game/fg1.png');
    PIXI.Loader.shared.resources.obstacle0 || PIXI.Loader.shared.add('obstacle0', 'game/przeszkoda0.png');
    PIXI.Loader.shared.resources.obstacle1 || PIXI.Loader.shared.add('obstacle1', 'game/przeszkoda1.png');
    PIXI.Loader.shared.resources.obstacle2 || PIXI.Loader.shared.add('obstacle2', 'game/przeszkoda2.png');
    PIXI.Loader.shared.resources.obstacle3 || PIXI.Loader.shared.add('obstacle3', 'game/przeszkoda3.png');
    PIXI.Loader.shared.resources.obstacle4 || PIXI.Loader.shared.add('obstacle4', 'game/przeszkoda4.png');
    PIXI.Loader.shared.resources.life || PIXI.Loader.shared.add('life', 'game/life.png');
    PIXI.Loader.shared.resources['energy-bar'] || PIXI.Loader.shared.add('energy-bar', 'game/energy-bar.png');
    PIXI.Loader.shared.resources['energy-border'] || PIXI.Loader.shared.add('energy-border', 'game/energy-border.png');
    PIXI.Loader.shared.resources.lifeIcon || PIXI.Loader.shared.add('lifeIcon', 'game/life-icon.png');

    PIXI.Loader.shared.load(() => {
      this.background.onResourcesLoaded();
      this.ground.onResourcesLoaded();
      this.player.onResourcesLoaded();
      this.foreground.onResourcesLoaded();
      this.health.onResourcesLoaded();
      this.energy.onResourcesLoaded();
      this.soundButton.onResourcesLoaded();

      this.start();
    });
  }

  update(dt) {
    const currentCellIndex = Math.floor(this.maxPlayerDistance / Config.GAME_CELL_SIZE);
    const isCellNew = currentCellIndex > this.lastCellIndex;

    const shouldFollowPlayer = this.player.positionX > Config.GAME_WIDTH / 4;
    const entities = [this.background, this.foreground, this.player, this.ground, ...this.treasures.children];

    this.health.setValue(this.player.lives);
    this.energy.setValue(this.player.energy);

    if (this.player.score !== this.lastScoreShown) {
      this.score.text = Math.floor(this.player.score).toString();
      this.lastScoreShown = this.player.score;
    }

    if (isCellNew) {
      const hasTreasure = Math.random() < Config.TREASURE_CHANCE;
      const hasObstacle = currentCellIndex - this.lastObstacleCellIndex > 10 && Math.random() < Config.OBSTACLE_CHANCE;

      if (hasTreasure) {
        const treasureX = Config.GAME_WIDTH + currentCellIndex * Config.GAME_CELL_SIZE + Config.GAME_CELL_SIZE / 2;
        const treasureY = Config.TREASURE_POSITION_LOW + Math.random() * (Config.TREASURE_POSITION_HIGH - Config.TREASURE_POSITION_LOW);

        const isLife = Math.random() < Config.LIFE_CHANCE;
        const treasureOrLife = isLife
          ? new Life(treasureX, treasureY)
          : new Treasure(treasureX, treasureY);

        this.treasures.addChild(treasureOrLife);
      }

      if (hasObstacle) {
        const obstacleX = Config.GAME_WIDTH + currentCellIndex * Config.GAME_CELL_SIZE + Config.GAME_CELL_SIZE / 2;

        this.lastObstacleCellIndex = currentCellIndex;
        this.obstacles.addChild(new Obstacle(obstacleX));
      }

      this.lastCellIndex = currentCellIndex;
    }

    entities.forEach((entity) => entity.update(dt, entities, this.viewport));
    // TODO: cleanup
    this.obstacles.children.forEach((obstacle) => obstacle.update(dt, entities, this.viewport));

    this.maxPlayerDistance = Math.max(this.maxPlayerDistance, this.player.positionX);

    this.treasures.children.forEach((treasure) => {
      const shouldDestroy = treasure.position.x < -Config.GAME_WIDTH / 2;

      if (shouldDestroy) {
        treasure.destroy();
      }
    });

    this.obstacles.children.forEach((obstacle) => {
      const shouldDestroy = obstacle.position.x < -Config.GAME_WIDTH / 2;

      if (shouldDestroy) obstacle.destroy();
    });
  }
}
